import { useRef, useState, useEffect } from 'react'
import type { IntroLang } from '../../hooks/useUserLanguage'

interface IntroVideoCircleProps {
  therapistType: string
  lang: IntroLang
  size?: 'sm' | 'md' | 'lg'
  autoPlay?: boolean
  fallbackAvatarUrl?: string
}

const sizeClasses = {
  sm: 'w-12 h-12',
  md: 'w-20 h-20',
  lg: 'w-32 h-32',
}

const borderColors: Record<string, string> = {
  wendy: 'border-wendy',
  artem: 'border-artem',
  finch: 'border-finch',
  una: 'border-una',
}

const playIconSizes = {
  sm: 'w-5 h-5',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
}

export default function IntroVideoCircle({
  therapistType,
  lang,
  size = 'md',
  autoPlay = false,
  fallbackAvatarUrl,
}: IntroVideoCircleProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasError, setHasError] = useState(false)

  const videoSrc = `/intro-videos/${therapistType}_${lang}_heygen-av4.mp4`
  const border = borderColors[therapistType] || 'border-primary-500'

  useEffect(() => {
    if (autoPlay && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Browser blocked autoplay — show as paused (first frame)
      })
    }
  }, [autoPlay])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
    } else {
      if (video.muted) video.muted = false
      video.play().catch(() => {})
    }
  }

  if (hasError) {
    return (
      <div className={`video-circle ${sizeClasses[size]} border-4 ${border} overflow-hidden bg-neutral-100`}>
        {fallbackAvatarUrl ? (
          <img src={fallbackAvatarUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-lg font-medium">
            {therapistType.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    )
  }

  return (
    <button onClick={handleClick} className="relative group">
      <div
        className={`video-circle ${sizeClasses[size]} border-4 ${border}
                   overflow-hidden shadow-lg transition-all duration-300
                   hover:scale-105 active:scale-95
                   ${isPlaying ? 'scale-105 shadow-xl ring-4 ring-primary-400/40' : ''}`}
      >
        <video
          ref={videoRef}
          src={videoSrc}
          className="w-full h-full object-cover"
          playsInline
          muted
          preload="auto"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onError={() => setHasError(true)}
        />
      </div>

      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full">
          <svg
            className={`${playIconSizes[size]} text-white drop-shadow-lg`}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
    </button>
  )
}
